import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import React, { useEffect, useMemo, useState } from "react";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import ComboService from "../../services/ComboService";
import { CustomSnackbarTypes } from "./CustomSnackBar";

interface ExtendCouponExpiryDialogProps {
  open: boolean;
  comboOrderId?: number;
  defaultExtendDays?: number;
  onClose: () => void;
  onSuccess: () => void;
}

const comboService = new ComboService();

const ExtendCouponExpiryDialog = ({
  open,
  comboOrderId,
  defaultExtendDays = 7,
  onClose,
  onSuccess,
}: ExtendCouponExpiryDialogProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [extendDaysInput, setExtendDaysInput] = useState<string>(
    String(defaultExtendDays),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState<string>("");

  useEffect(() => {
    if (!open) {
      setExtendDaysInput(String(defaultExtendDays));
      setIsSubmitting(false);
      setFieldError("");
    }
  }, [open, defaultExtendDays]);

  const extendDays = useMemo(
    () => Number.parseInt(extendDaysInput, 10),
    [extendDaysInput],
  );

  const validate = () => {
    if (!comboOrderId) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Combo order id is missing",
        type: CustomSnackbarTypes.ERROR,
      });
      return false;
    }

    if (!Number.isInteger(extendDays) || extendDays <= 0) {
      setFieldError("Enter a valid number greater than 0");
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Enter a valid extension days value greater than 0",
        type: CustomSnackbarTypes.INFO,
      });
      return false;
    }

    if (extendDays > 365) {
      setFieldError("Maximum allowed is 365 days");
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Extension days should be 365 or less",
        type: CustomSnackbarTypes.INFO,
      });
      return false;
    }

    setFieldError("");
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      await comboService.extendCouponExpiry(comboOrderId!, extendDays);
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Coupon expiry extended successfullyy",
        type: CustomSnackbarTypes.SUCCESS,
      });
      onSuccess();
      onClose();
    } catch (error) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Failed to extend coupon expiry",
        type: CustomSnackbarTypes.ERROR,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          width: "100%",
          borderRadius: { xs: 0, sm: 2 },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, fontWeight: 600 }}>Extend Coupon Expiry</DialogTitle>
      <DialogContent sx={{ paddingBottom: 0 }}>
        <Box>
          <TextField
            type="number"
            variant="outlined"
            size="small"
            value={extendDaysInput}
            onChange={(event) => {
              setExtendDaysInput(event.target.value);
              if (fieldError) setFieldError("");
            }}
            inputProps={{ min: 1, max: 365 }}
            error={Boolean(fieldError)}
            fullWidth
            autoFocus
            placeholder="e.g. 10"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#fff",
              },
              "& .MuiOutlinedInput-input": {
                py: 1.15,
              },
            }}
          />
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 0.75,
              ml: 0.25,
              color: fieldError ? "error.main" : "text.secondary",
            }}
          >
            {fieldError || "Enter number of days to extend the coupon expiry"}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          justifyContent: "flex-start",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
          sx={{ minWidth: 120 }}
        >
          {isSubmitting ? <CircularProgress size={18} color="inherit" /> : "Submit"}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onClose}
          disabled={isSubmitting}
          sx={{ minWidth: 120 }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExtendCouponExpiryDialog;
